import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FolderPlus, PencilLine, X } from "lucide-react";
import { Button } from "./ui";

type ProjectNameDialogProps = {
  open: boolean;
  mode: "create" | "rename";
  initialName?: string;
  onCancel: () => void;
  onConfirm: (name: string) => void;
};

export function ProjectNameDialog({
  open,
  mode,
  initialName = "",
  onCancel,
  onConfirm,
}: ProjectNameDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const onCancelRef = useRef(onCancel);
  const [name, setName] = useState(initialName);
  const [error, setError] = useState("");

  useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);

  useEffect(() => {
    if (!open) return;

    setName(initialName);
    setError("");
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();
    inputRef.current?.select();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancelRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [initialName, open]);

  if (!open) return null;

  const isCreate = mode === "create";
  const trimmedName = name.trim();
  const submitName = () => {
    if (!trimmedName) {
      setError("Enter a name for this project.");
      inputRef.current?.focus();
      return;
    }
    onConfirm(trimmedName);
  };

  return createPortal(
    <div
      className="dialog-backdrop"
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section
        className="confirm-dialog project-name-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header>
          <span className="confirm-dialog__icon project-name-dialog__icon" aria-hidden="true">
            {isCreate ? <FolderPlus size={20} /> : <PencilLine size={20} />}
          </span>
          <div>
            <h2 id={titleId}>{isCreate ? "Name your project" : "Rename project"}</h2>
            <p id={descriptionId}>
              {isCreate
                ? "Use a short name you will recognize on the Projects page."
                : "This changes the project name everywhere it appears in Paper Kingdom."}
            </p>
          </div>
          <button className="icon-button" type="button" aria-label="Close" onClick={onCancel}>
            <X size={18} />
          </button>
        </header>
        <div className="project-name-dialog__body">
          <div className="field">
            <label className="field__label" htmlFor={inputId}>Project name</label>
            <input
              ref={inputRef}
              id={inputId}
              className="input"
              value={name}
              maxLength={120}
              autoComplete="off"
              aria-describedby={`${hintId}${error ? ` ${errorId}` : ""}`}
              aria-invalid={error ? "true" : undefined}
              onChange={(event) => {
                setName(event.target.value);
                if (error) setError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  submitName();
                }
              }}
            />
            <span className="field__hint" id={hintId}>
              {isCreate ? "You can change this later." : "Use up to 120 characters."}
            </span>
          </div>
          {error ? (
            <p className="project-name-dialog__error" id={errorId} role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <footer>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" disabled={!trimmedName} onClick={submitName}>
            {isCreate ? "Create project" : "Save name"}
          </Button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
